import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Sparkles, Lock, Smartphone, Watch, Wind, Droplets, SprayCan, Bug, Hand, Sun, Flower2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";
import { SectionHeading, Reveal } from "@/components/Section";
import { ASSETS } from "@/lib/assets";
import Marquee from "@/components/Marquee";
import ZoomImage from "@/components/ZoomImage";
import BandExplorer from "@/components/BandExplorer";

const PLATFORM_A = "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/";

const GALLERY = [
  { src: `${PLATFORM_A}eqcorxrc_Screenshot%202026-08-07%20at%205.20.06%E2%80%AFAM.jpg`, alt: "Colorful WearSpray bands arranged on driftwood beside coral and a blue sand dollar", caption: "One platform, many uses" },
  { src: `${PLATFORM_A}10qs5ewa_958DC6CC-B130-492E-AFBE-AB6D5BE1FA6E_1_105_c.jpg`, alt: "A WearSpray band resting on rope by the ocean at sunset", caption: "Wherever the day takes you" },
  { src: `${PLATFORM_A}e9aq6ryf_army%20of%20bands2.jpg`, alt: "A colorful cluster of WearSpray band faces from above on a black background", caption: "A color for every mood" },
  { src: `${PLATFORM_A}c02bqevh_Screenshot%202026-08-07%20at%205.29.12%E2%80%AFAM.webp`, alt: "A blue WearSpray band resting on golden beach sand in the sun", caption: "Ready for the outdoors" },
];

const EXPLORE_CARDS = [
  {
    to: "/why",
    eyebrow: "The Case",
    title: "Why WearSpray",
    body: "Why reapplication fails without a wearable, and how closing that gap changes daily behavior.",
  },
  {
    to: "/products",
    eyebrow: "The Product",
    title: "Safe Band Pro",
    body: "The refillable wrist dispenser already manufactured, sold and worn by customers today.",
  },
  {
    to: "/technology",
    eyebrow: "The Mechanism",
    title: "How It Works",
    body: "Inside the patented valve and reservoir architecture that makes one-press dispensing possible.",
  },
];

export default function Platform() {
  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden" data-testid="platform-page">
      <SEO {...PAGE_SEO["/platform"]} path="/platform" />

      {/* HERO */}
      <section className="relative border-b border-zinc-800 overflow-hidden" data-testid="platform-hero">
        <div className="absolute inset-0 z-0">
          <img
            loading="eager"
            src="https://images.unsplash.com/photo-1581092921461-eab62e97a780?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Precision engineering workbench with glowing teal tools"
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 pt-28 pb-16 md:pt-36 md:pb-20">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-7">
              The Platform
            </span>
            <h1 className="font-heading font-light tracking-tight leading-[1.1] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              What WearSpray physically enables.
            </h1>
            <p className="font-body text-zinc-400 text-lg md:text-xl leading-relaxed mt-7 max-w-2xl">
              A patented dispensing mechanism, a manufactured wrist dispenser already shipping
              today, and future concepts, a phone-mounted reservoir and an attachable smartwatch
              pod, that extend the same architecture further.
            </p>
          </Reveal>
        </div>
      </section>

      <BandExplorer variant="platform" />

      {/* AVAILABLE TODAY vs IN DEVELOPMENT */}
      <section className="border-t border-zinc-900 bg-[#0a0a0a]" data-testid="platform-ecosystem-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="One Platform, Built In Stages"
            title="Manufactured today. Expanding tomorrow."
            subtitle="Some of this is patented and already in production. Some of it is a future format of the same architecture. We think it's important to be clear about which is which."
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-14">
            <Reveal>
              <div data-testid="platform-available-card" className="h-full border border-accent/30 bg-accent/[0.04] rounded-xl p-7">
                <div className="inline-flex items-center gap-2 bg-accent text-black px-3 py-1 rounded-full">
                  <BadgeCheck size={13} />
                  <span className="font-mono text-[10px] tracking-[0.15em] uppercase font-bold">Available Today</span>
                </div>
                <h3 className="font-heading text-xl font-medium text-white mt-5">Safe Band Pro wrist dispenser</h3>
                <p className="font-body text-sm text-zinc-300 leading-relaxed mt-3">
                  A patented, refillable wrist-worn dispenser that puts a chosen formulation,
                  sunscreen, lotion, repellent or sanitizer, one press away. Manufactured and
                  shipping today, not a rendering.
                </p>
                <div className="mt-6">
                  <img
                    loading="lazy"
                    src={ASSETS.techExploded}
                    alt="Exploded view of the Safe Band Pro valve and reservoir mechanism"
                    className="w-full max-h-[280px] object-contain rounded-lg"
                  />
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div data-testid="platform-development-card" className="h-full border border-zinc-800 bg-[#0d0d0d] rounded-xl p-7">
                <div className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 px-3 py-1 rounded-full">
                  <Sparkles size={13} />
                  <span className="font-mono text-[10px] tracking-[0.15em] uppercase font-bold">In Development</span>
                </div>
                <div className="mt-6 flex items-start gap-4">
                  <Smartphone size={19} className="text-accent shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-heading text-base font-medium text-white">Phone-mounted reservoir</h4>
                    <p className="font-body text-sm text-zinc-400 leading-relaxed mt-1.5">
                      A proposed reservoir format that rides along on a device people already carry everywhere.
                    </p>
                  </div>
                </div>
                <div className="mt-6 flex items-start gap-4">
                  <Watch size={19} className="text-accent shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-heading text-base font-medium text-white">Attachable smartwatch pod</h4>
                    <p className="font-body text-sm text-zinc-400 leading-relaxed mt-1.5">
                      A proposed modular pod designed to attach to a smartwatch band or other everyday accessory.
                    </p>
                  </div>
                </div>
                <div className="mt-6 flex items-start gap-4">
                  <Lock size={19} className="text-accent shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-heading text-base font-medium text-white">Sealed, single-formulation cartridges</h4>
                    <p className="font-body text-sm text-zinc-400 leading-relaxed mt-1.5">
                      A proposed packaging variant of the same reservoir, tamper-evident and swapped as a whole unit for clinical or institutional use.
                    </p>
                  </div>
                </div>
                <p className="font-body text-xs text-zinc-600 leading-relaxed mt-7 border-t border-zinc-800 pt-5">
                  These are concepts and development directions, not shipping products.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THE CORE MECHANISM, quick credibility */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="platform-mechanism-section">
        <SectionHeading
          overline="Simple To See. Hard To Invent."
          title="Two precision-engineered components earned our patent."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-14">
          <Reveal>
            <div className="h-full border border-zinc-800 bg-[#0d0d0d] p-7 rounded-xl">
              <Wind size={22} className="text-accent" />
              <h3 className="font-heading text-lg font-medium text-white mt-5">The Breathing Valve</h3>
              <p className="font-body text-sm text-zinc-400 leading-relaxed mt-4">
                A precision valve seated inside the reservoir in a way that lets it breathe, the breakthrough that took years to engineer and earned our patent.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="h-full border border-zinc-800 bg-[#0d0d0d] p-7 rounded-xl">
              <Droplets size={22} className="text-accent" />
              <h3 className="font-heading text-lg font-medium text-white mt-5">Adaptable Formulation Reservoir</h3>
              <p className="font-body text-sm text-zinc-400 leading-relaxed mt-4">
                A refillable reservoir built for the skincare you reapply through the day, ready to carry the products you already trust.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CREDIBILITY STRIP */}
      <section className="border-y border-zinc-800 bg-[#0d0d0d]" data-testid="platform-credibility">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 md:gap-x-14">
            {["Patented Technology", "Refillable, Not Single-Use", "Skin-Safe Materials", "Made for Daily Wear"].map((t, i) => (
              <div key={t} className="flex items-center gap-8 md:gap-14">
                {i > 0 && <span className="w-1 h-1 rounded-full bg-accent/60 -ml-4 md:-ml-8" />}
                <span className="font-mono text-[11px] md:text-xs tracking-[0.22em] uppercase text-zinc-400">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* CONFIDENCE QUOTE */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <Reveal>
          <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-light leading-[1.4] tracking-tight max-w-4xl">
            WearSpray turns the wrist into a new point of access for skincare. From sunscreen
            and daily moisturizer to body spray, our patented refillable platform keeps the
            products you rely on <span className="text-accent">wearable, accessible, and ready</span> the
            moment you need them.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-12 border-l-2 border-accent pl-6 md:pl-10 max-w-3xl">
            <p className="font-body text-lg text-zinc-300 leading-relaxed">
              We hear the same thing from customers again and again:{" "}
              <span className="text-accent">
                once they start wearing WearSpray, they do not want to leave home without it.
              </span>
            </p>
          </div>
        </Reveal>
      </section>

      {/* CATEGORY GRID */}
      <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="platform-categories">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent block mb-6">Fill It With What You Need</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tighter leading-[1.05] max-w-3xl">
              One platform. <span className="text-accent">Many formulations.</span>
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-2xl">
              Refillable by design: fill it with what you already trust, and refill it again and again, less waste, always ready, always yours.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px mt-14 bg-zinc-800 border border-zinc-800">
            {[
              { icon: Droplets, label: "Moisturizer" },
              { icon: Sparkles, label: "Skincare" },
              { icon: Flower2, label: "Serum" },
              { icon: SprayCan, label: "Body Spray" },
              { icon: Sun, label: "Sunscreen" },
              { icon: Wind, label: "After-Sun" },
              { icon: Bug, label: "Repellent" },
              { icon: Hand, label: "Sanitizer" },
            ].map((c, i) => (
              <Reveal key={c.label} delay={(i % 4) * 0.05}>
                <div className="bg-[#121212] p-8 h-full flex flex-col items-center text-center hover:bg-[#161616] transition-colors" data-testid={`platform-cat-${i}`}>
                  <c.icon size={26} strokeWidth={1.4} className="text-accent" />
                  <span className="font-mono text-xs tracking-[0.16em] uppercase text-zinc-300 mt-5">{c.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT GALLERY */}
      <section className="border-t border-zinc-800 bg-black" data-testid="platform-gallery">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading
            overline="The Range"
            title="Made to be seen, and worn."
            subtitle="From tropical mornings to golden-hour evenings, WearSpray goes where the day goes."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px mt-16 bg-zinc-800 border border-zinc-800">
            {GALLERY.map((g, i) => (
              <Reveal key={g.src} delay={(i % 4) * 0.08}>
                <div className="relative bg-black overflow-hidden group h-full">
                  <ZoomImage
                    src={g.src}
                    alt={g.alt}
                    caption={g.caption}
                    testId={`platform-gallery-img-${i}`}
                    className="w-full h-64 md:h-72 object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
                  <p className="absolute bottom-0 left-0 p-4 font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-300 pointer-events-none">{g.caption}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LUXURY BANNER */}
      <section className="relative w-full overflow-hidden" data-testid="platform-luxury-banner">
        <img loading="lazy"
          src="https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/hn1acboi_elegant%20woman.jpg"
          alt="An elegant woman at a gala wearing a luxury WearSpray band with a glowing face"
          className="w-full h-[50vh] md:h-[64vh] object-cover"
          style={{ objectPosition: "center 22%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-black/40 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-full">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pb-10 md:pb-14">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-accent">The Future of Wearable Luxury</p>
              <p className="font-heading text-2xl md:text-4xl font-light tracking-tight text-white mt-3 max-w-2xl leading-snug">
                From everyday protection to the world's most refined rituals.
              </p>
            </Reveal>
          </div>
        </div>
      </section>


      {/* EXPLORE DEEPER */}
      <section className="border-t border-zinc-900 bg-[#0a0a0a]" data-testid="platform-explore-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <SectionHeading overline="Go Deeper" title="Explore the platform in detail." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {EXPLORE_CARDS.map((c, i) => (
              <Reveal key={c.to} delay={i * 0.08}>
                <Link
                  to={c.to}
                  data-testid={`platform-explore-card-${i}`}
                  className="group block h-full border border-zinc-800 bg-[#0d0d0d] p-7 rounded-xl hover:border-accent/40 transition-colors"
                >
                  <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent">{c.eyebrow}</span>
                  <h3 className="font-heading text-xl font-medium text-white mt-4">{c.title}</h3>
                  <p className="font-body text-sm text-zinc-400 leading-relaxed mt-3">{c.body}</p>
                  <span className="inline-flex items-center gap-2 font-body text-sm text-white mt-6 group-hover:gap-3 transition-all">
                    Visit page <ArrowRight size={14} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center" data-testid="platform-cta-section">
        <Reveal>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-3xl mx-auto">
            Build on a platform that's already manufactured and shipping.
          </h2>
          <Link
            to="/contact"
            data-testid="platform-cta-link"
            className="inline-flex items-center gap-2 font-body text-sm font-medium bg-[#00e5ff] text-black px-8 py-4 mt-9 hover:bg-[#00b3cc] transition-colors"
          >
            Partner With Us
            <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
